import Link from 'next/link';
import React, { useCallback, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import ChartColoredSvg from '../assets/icons/ChartColoredSvg';
import CrownColoredSvg from '../assets/icons/CrownColoredSvg';
import EnterprisePlanIcon from '../assets/icons/EnterprisePlanIcon';
import GlobeColoredSvg from '../assets/icons/GlobeColoredSvg';
import { ListColoredSvg } from '../assets/icons/ListColoredSvg';
import Logo from '../assets/icons/Logo';
import theme from '../assets/theme';
import { Button } from '../components/_atoms/Button';
import { IconWrapper } from '../components/_atoms/IconWrapper';
import BaseModal from '../components/_molecules/BaseModal';
import {
  TexasSchoolPlanPaywall,
  CaliforniaSchoolPlanPaywall,
} from '../components/_organisms/StateSpecificTestPrepPaywall';
import Metrics from '../services/metrics';
import useAccessBadge from './useAccessBadge';
import { useMetrics } from './useMetrics';

type PaywallVariant = 'default' | 'TX' | 'CA';

function isPaywallVariant(value: string | undefined): value is PaywallVariant {
  return value === 'TX' || value === 'CA';
}

const FeatureCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 10px;
  cursor: pointer;
  transition: transform 0.2s;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  display: grid;
  grid-template-rows: auto 60px;

  &:hover {
    transform: scale(1.05);
  }
`;

const SchoolPlanPaywall = () => {
  const { t } = useTranslate();
  const { showNewMessages } = useIntercom();
  const { accessBadge } = useAccessBadge();

  const canPurchaseIndividual = accessBadge?.canPurchaseIndividualPremium ?? false;

  const features = [
    {
      title: t('SchoolPlanPaywall.UnlimitedAIAccess', 'Unlimited Curipod AI Access'),
      icon: <CrownColoredSvg />,
    },
    {
      title: t('SchoolPlanPaywall.BilingualSupport', 'Bilingual Support'),
      icon: <GlobeColoredSvg />,
    },
    {
      title: canPurchaseIndividual
        ? t('SchoolPlanPaywall.StoryBasedQuickwrites', 'Story based quickwrites')
        : t('SchoolPlanPaywall.TestPrep', 'Test Prep'),
      icon: <ListColoredSvg />,
    },
    {
      title: t('SchoolPlanPaywall.StudentInsightReports', 'Student insight reports'),
      icon: <ChartColoredSvg />,
    },
  ];

  const title = canPurchaseIndividual
    ? t('SchoolPlanPaywall.UnlockUnlimitedAccess', 'Unlock unlimited access')
    : t('SchoolPlanPaywall.UnlockSchoolPlan', 'Unlock the School Plan!');

  const subtitle = canPurchaseIndividual
    ? t(
        'SchoolPlanPaywall.ViewAvailablePlans',
        'View available plans to get the full curipod experience.',
      )
    : t(
        'SchoolPlanPaywall.AskAdministrator',
        'To get full access to Curipod, ask your school administrator about upgrading to the school plan.',
      );

  return (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            color: theme.colors.orange,
            width: '150px',
            height: '42px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Logo style={{ width: '100%', height: '100%' }} />
        </div>
        {!canPurchaseIndividual && (
          <span
            style={{
              background: theme.colors.orange,
              color: 'white',
              padding: '4px 12px',
              borderRadius: '4px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <EnterprisePlanIcon width="20px" height="20px" style={{ display: 'block' }} />
            <span style={{ display: 'block' }}>{t('SCHOOL PLAN')}</span>
          </span>
        )}
      </div>

      <h1
        style={{
          fontSize: '36px',
          marginBottom: '30px',
          lineHeight: '1.2',
        }}
      >
        {title}
      </h1>

      <h2
        style={{
          fontSize: '24px',
          marginBottom: '40px',
          lineHeight: '1.3',
          fontWeight: 'bold',
        }}
      >
        {subtitle}
      </h2>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        {features.map((feature, index) => (
          <FeatureCard key={index}>
            <p
              style={{
                fontWeight: 'bold',
                fontSize: '16px',
                marginBottom: '20px',
                color: theme.colors.black,
              }}
            >
              {feature.title}
            </p>

            <IconWrapper height="60px" width="60px">
              {feature.icon}
            </IconWrapper>
          </FeatureCard>
        ))}
      </div>
      <div style={{ marginBottom: 60 }}></div>
      <ButtonContainer>
        <div
          style={{
            display: 'flex',
            gap: '15px',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <Button
            type="ghost"
            onClick={() => {
              Metrics.getLogger().logEvent('SchoolPlanPaywall.ChatWithUs');
              showNewMessages(
                canPurchaseIndividual
                  ? 'Help me unlock unlimited access.'
                  : 'Help me unlock the school plan.',
              );
            }}
          >
            {t('SchoolPlanPaywall.ChatWithUs', 'Chat with us')}
          </Button>
          <Link href={canPurchaseIndividual ? '/pricing' : '/quotes'} passHref>
            <a>
              <Button
                type="primary"
                onClick={() => {
                  Metrics.getLogger().logEvent(
                    canPurchaseIndividual ? 'Paywall.SeePricing' : 'Paywall.RequestQuote',
                  );
                }}
                styles={{
                  backgroundColor: theme.colors.orange,
                  borderColor: theme.colors.orange,
                }}
              >
                {canPurchaseIndividual
                  ? t('SchoolPlanPaywall.SeePricing', 'See pricing')
                  : t('SchoolPlanPaywall.RequestQuote', 'Request a quote')}
              </Button>
            </a>
          </Link>
        </div>
        {!canPurchaseIndividual && (
          <p style={{ fontSize: 14, marginBottom: 10, marginTop: 30 }}>
            {t('SchoolPlanPaywall.UseThis', 'Use this')}{' '}
            <Link href="/curipod_teacher_pitch.pdf" passHref>
              <a
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  Metrics.getLogger().logEvent('Paywall.Get1Pager');
                  window.open(
                    '/curipod_teacher_pitch.pdf',
                    '_blank',
                    'noopener,noreferrer',
                  );
                }}
              >
                <ChatLink>
                  {t('SchoolPlanPaywall.ReadyToShare1Pager', 'ready-to-share 1-pager')}
                </ChatLink>
              </a>
            </Link>{' '}
            {t(
              'SchoolPlanPaywall.ShowAdministratorDifference',
              'to show your administrator how Curipod is making a difference in your classroom.',
            )}
          </p>
        )}
      </ButtonContainer>
    </div>
  );
};

const TestPrepPaywall = () => {
  const { t } = useTranslate();
  const { showNewMessages } = useIntercom();
  const { accessBadge } = useAccessBadge();

  const canPurchaseIndividual = accessBadge?.canPurchaseIndividualPremium ?? false;

  const features = [
    {
      title: t('SchoolPlanPaywall.UnlimitedAIAccess', 'Unlimited Curipod AI Access'),
      icon: <CrownColoredSvg />,
    },
    {
      title: t('SchoolPlanPaywall.BilingualSupport', 'Bilingual Support'),
      icon: <GlobeColoredSvg />,
    },
    {
      title: canPurchaseIndividual
        ? t('SchoolPlanPaywall.StoryBasedQuickwrites', 'Story based quickwrites')
        : t('SchoolPlanPaywall.TestPrep', 'Test Prep'),
      icon: <ListColoredSvg />,
    },
    {
      title: t('SchoolPlanPaywall.StudentInsightReports', 'Student insight reports'),
      icon: <ChartColoredSvg />,
    },
  ];

  const title = canPurchaseIndividual
    ? t('SchoolPlanPaywall.UnlockUnlimitedAccess', 'Unlock unlimited access')
    : t('SchoolPlanPaywall.UnlockSchoolPlan', 'Test Prep Generation Limit Reached');

  const subtitle = canPurchaseIndividual
    ? t(
        'SchoolPlanPaywall.ViewAvailablePlans',
        'View available plans to get the full curipod experience.',
      )
    : t(
        'SchoolPlanPaywall.AskAdministrator',
        'To get full access to Curipod, ask your school administrator about upgrading to the school plan.',
      );

  return (
    <div style={{ textAlign: 'center', padding: '20px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            color: theme.colors.orange,
            width: '150px',
            height: '42px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Logo style={{ width: '100%', height: '100%' }} />
        </div>
        {!canPurchaseIndividual && (
          <span
            style={{
              background: theme.colors.orange,
              color: 'white',
              padding: '4px 12px',
              borderRadius: '4px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
            }}
          >
            <EnterprisePlanIcon width="20px" height="20px" style={{ display: 'block' }} />
            <span style={{ display: 'block' }}>{t('SCHOOL PLAN')}</span>
          </span>
        )}
      </div>

      <h1
        style={{
          fontSize: '36px',
          marginBottom: '30px',
          lineHeight: '1.2',
        }}
      >
        {title}
      </h1>

      <h2
        style={{
          fontSize: '24px',
          marginBottom: '40px',
          lineHeight: '1.3',
          fontWeight: 'bold',
        }}
      >
        {subtitle}
      </h2>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px',
        }}
      >
        {features.map((feature, index) => (
          <FeatureCard key={index}>
            <p
              style={{
                fontWeight: 'bold',
                fontSize: '16px',
                marginBottom: '20px',
                color: theme.colors.black,
              }}
            >
              {feature.title}
            </p>

            <IconWrapper height="60px" width="60px">
              {feature.icon}
            </IconWrapper>
          </FeatureCard>
        ))}
      </div>
      <div style={{ marginBottom: 60 }}></div>
      <ButtonContainer>
        <div
          style={{
            display: 'flex',
            gap: '15px',
            flexWrap: 'wrap',
            justifyContent: 'center',
          }}
        >
          <Button
            type="ghost"
            onClick={() => {
              Metrics.getLogger().logEvent('SchoolPlanPaywall.ChatWithUs');
              showNewMessages(
                canPurchaseIndividual
                  ? 'Help me unlock unlimited access.'
                  : 'Help me unlock the school plan.',
              );
            }}
          >
            {t('SchoolPlanPaywall.ChatWithUs', 'Chat with us')}
          </Button>
          <Link href={canPurchaseIndividual ? '/pricing' : '/quotes'} passHref>
            <a>
              <Button
                type="primary"
                onClick={() => {
                  Metrics.getLogger().logEvent(
                    canPurchaseIndividual ? 'Paywall.SeePricing' : 'Paywall.RequestQuote',
                  );
                }}
                styles={{
                  backgroundColor: theme.colors.orange,
                  borderColor: theme.colors.orange,
                }}
              >
                {canPurchaseIndividual
                  ? t('SchoolPlanPaywall.SeePricing', 'See pricing')
                  : t('SchoolPlanPaywall.RequestQuote', 'Request a quote')}
              </Button>
            </a>
          </Link>
        </div>
        {!canPurchaseIndividual && (
          <p style={{ fontSize: 14, marginBottom: 10, marginTop: 30 }}>
            {t('SchoolPlanPaywall.UseThis', 'Use this')}{' '}
            <Link href="/curipod_teacher_pitch.pdf" passHref>
              <a
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  Metrics.getLogger().logEvent('Paywall.Get1Pager');
                  window.open(
                    '/curipod_teacher_pitch.pdf',
                    '_blank',
                    'noopener,noreferrer',
                  );
                }}
              >
                <ChatLink>
                  {t('SchoolPlanPaywall.ReadyToShare1Pager', 'ready-to-share 1-pager')}
                </ChatLink>
              </a>
            </Link>{' '}
            {t(
              'SchoolPlanPaywall.ShowAdministratorDifference',
              'to show your administrator how Curipod is making a difference in your classroom.',
            )}
          </p>
        )}
      </ButtonContainer>
    </div>
  );
};

const ChatLink = styled.span`
  font-size: 14px;
  color: ${theme.colors.black};
  cursor: pointer;
  text-decoration: underline;
  margin-top: 5px;
  transition: all 0.2s ease;

  &:hover {
    color: ${theme.colors.orange};
    text-decoration: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;

  @media (max-width: 480px) {
    width: 100%;
    gap: 10px;

    & > button {
      width: 100%;
      max-width: 250px;
    }
  }
`;

const PaywallByVariant = ({ variant }: { variant: PaywallVariant }) => {
  switch (variant) {
    case 'TX':
      return <TexasSchoolPlanPaywall />;
    case 'CA':
      return <CaliforniaSchoolPlanPaywall />;
    case 'default':
      return <TestPrepPaywall />;
    default:
      return <SchoolPlanPaywall />;
  }
};

function usePaywallPrompt({
  startState,
  onClose,
}: {
  startState?: { screen: string };
  onClose?: () => void;
}) {
  const { accessBadge: userAccessBadge } = useAccessBadge();
  const metrics = useMetrics();
  const [show, setShow] = useState(false);
  const [variant, setVariant] = useState<PaywallVariant>('default');

  const isReady = userAccessBadge !== undefined;

  const shouldPromptPaywall = useCallback(() => {
    return false; // Simplified - no longer prompting automatically
  }, []);

  const prompt = useCallback((metadata?: PaywallMetadata) => {
    const variant = metadata?.variant;
    const derivedVariant = variant && isPaywallVariant(variant) ? variant : 'default';

    metrics.logEvent('Paywall.Prompted', {
      ...metadata,
      screen: startState?.screen,
      state: derivedVariant,
    });
    setVariant(derivedVariant);
    setShow(true);
  }, []);

  const hide = useCallback(() => {
    setShow(false);
    setVariant('default');
    onClose && onClose();
  }, [onClose]);

  const component = (
    <BaseModal width="1000px" title="" visible={show} onCancel={hide} style={{ top: 10 }}>
      <PaywallByVariant variant={variant} />
    </BaseModal>
  );

  return {
    promptPaywall: (metadata?: PaywallMetadata) => prompt(metadata),
    hidePaywall: hide,
    paywallComponent: component,
    shouldPromptPaywall,
    isReady,
    isShowingPaywallPrompt: show,
    userAccessBadge,
    tenantName: userAccessBadge?.organization?.name,
  };
}

export type PaywallMetadata = {
  label?: string;
  description?: string;
  screen?: string;
  variant?: string;
};

export default usePaywallPrompt;
